import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { api } from "../lib/api";
import { useContent } from "../context/ContentContext";
import { Siren, FileDown, Map, Phone, Activity, MapPin, ArrowRight, Newspaper, ShieldCheck, HeartHandshake } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1632019475103-15fffd9dbd7d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxpbmRvbmVzaWElMjBsYW5kc2NhcGUlMjB0cm9waWNhbHxlbnwwfHx8fDE3ODM3NzU1Njd8MA&ixlib=rb-4.1.0&q=85";

const CountUp = ({ value }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    if (!value) { setN(value || 0); return; }
    const steps = 35;
    let i = 0;
    const t = setInterval(() => {
      i++;
      setN(Math.round((value * i) / steps));
      if (i >= steps) clearInterval(t);
    }, 28);
    return () => clearInterval(t);
  }, [inView, value]);
  return <span ref={ref}>{n}</span>;
};

const rise = {
  hidden: { opacity: 0, y: 28 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.55, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Home() {
  const { c } = useContent();
  const [stats, setStats] = useState(null);
  const [news, setNews] = useState([]);
  const [kegiatan, setKegiatan] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  useEffect(() => {
    api.get("/disasters/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/news").then((r) => {
      setNews(r.data.filter((n) => n.category !== "Kegiatan").slice(0, 4));
      setKegiatan(r.data.filter((n) => n.category === "Kegiatan").slice(0, 3));
    }).catch(() => {});
  }, []);

  const bentoStats = [
    { icon: Activity, color: "text-orange-600 bg-orange-50", value: stats?.total ?? 0, label: "Total Kejadian Tercatat", testId: "stat-total-kejadian" },
    { icon: Siren, color: "text-red-600 bg-red-50", value: stats?.active ?? 0, label: "Dalam Penanganan", testId: "stat-aktif" },
    { icon: MapPin, color: "text-sky-600 bg-sky-50", value: 6, label: "Kecamatan Dipantau", testId: "stat-wilayah" },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section ref={heroRef} className="relative bg-slate-950 text-white overflow-hidden">
        <motion.img src={c("hero_foto", HERO_IMG)} alt="Lanskap Kayong Utara" style={{ y: heroY }} className="absolute inset-0 w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-slate-900/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/60 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 pt-24 pb-40 lg:pt-32 lg:pb-48">
          <div className="flex items-center gap-3 mb-6 fade-up">
            <img src="/logo-bpbd.png" alt="Logo BPBD" className="w-14 h-14 object-contain floaty" />
            <p className="section-label text-orange-400">Siap Untuk Selamat</p>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold max-w-3xl leading-[1.05]">
            <span className="reveal-line"><span>Melindungi Kayong Utara,</span></span>
            <span className="reveal-line"><span className="grad-text">Tangguh Hadapi Bencana</span></span>
          </h1>
          <p className="mt-6 text-slate-300 max-w-xl text-base md:text-lg fade-up-2">
            Badan Penanggulangan Bencana Daerah Kabupaten Kayong Utara — pencegahan, kesiapsiagaan, penanganan darurat, serta pemulihan pasca bencana.
          </p>
          <div className="mt-9 flex flex-wrap gap-3 fade-up-3">
            <Link to="/layanan#lapor" className="pulse-glow btn-pop inline-flex items-center gap-2 px-7 py-3.5 bg-orange-600 hover:bg-orange-500 font-bold rounded-full" data-testid="hero-lapor-btn">
              <Siren size={18} /> Lapor Bencana
            </Link>
            <Link to="/informasi" className="btn-pop inline-flex items-center gap-2 px-7 py-3.5 bg-white/10 backdrop-blur border border-white/25 hover:bg-white/20 font-semibold rounded-full" data-testid="hero-peta-btn">
              <Map size={18} /> Peta Risiko
            </Link>
            <a href="https://bnpb.go.id" target="_blank" rel="noreferrer" className="btn-pop inline-flex items-center gap-2 px-7 py-3.5 bg-white/10 backdrop-blur border border-white/25 hover:bg-white/20 font-semibold rounded-full" data-testid="hero-panduan-btn">
              <FileDown size={18} /> Unduh Panduan
            </a>
          </div>
        </div>
      </section>

      {/* Overlapping bento stats */}
      <section className="max-w-7xl mx-auto px-4 -mt-24 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {bentoStats.map((s, i) => (
            <motion.div key={s.label} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={rise} className="bento-card p-7" data-testid={s.testId}>
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${s.color}`}>
                <s.icon size={22} />
              </div>
              <p className="font-mono text-4xl font-bold text-slate-900"><CountUp value={s.value} /></p>
              <p className="text-sm text-slate-500 mt-1.5 font-medium">{s.label}</p>
            </motion.div>
          ))}
          <motion.div custom={3} initial="hidden" whileInView="show" viewport={{ once: true }} variants={rise} className="bento-card p-7 !bg-slate-950 !border-slate-800 text-white" data-testid="stat-darurat">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 bg-red-500/15 text-red-400">
              <Phone size={22} />
            </div>
            <p className="font-mono text-4xl font-bold text-orange-500">{c("kontak_darurat", "112")}</p>
            <p className="text-sm text-slate-400 mt-1.5 font-medium">Call Center Darurat 24 Jam</p>
          </motion.div>
        </div>
      </section>

      {/* News + emergency bento */}
      <section className="max-w-7xl mx-auto px-4 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="section-label text-orange-600 mb-2">Terkini</p>
            <h2 className="font-heading text-lg font-bold text-slate-900">Berita & Pengumuman</h2>
          </div>
          <Link to="/berita" className="group text-sm font-bold text-sky-600 hover:text-orange-600 inline-flex items-center gap-1.5 transition-colors" data-testid="home-semua-berita">
            Semua Berita <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-6">
            {news.length === 0 && (
              <div className="sm:col-span-2 border-2 border-dashed border-slate-200 rounded-xl p-12 text-center text-slate-400" data-testid="news-empty">
                <Newspaper className="mx-auto mb-3" size={30} />
                Belum ada berita yang dipublikasikan.
              </div>
            )}
            {news.map((n, i) => (
              <motion.div key={n.id} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={rise} className={i === 0 && news.length > 2 ? "sm:col-span-2" : ""}>
                <Link to={`/berita/${n.id}`} className="group hover-lift flex flex-col h-full border border-slate-200 bg-white rounded-xl overflow-hidden hover:border-orange-400" data-testid={`news-card-${n.id}`}>
                  {n.image_url && <img src={n.image_url} alt={n.title} className={`w-full object-cover ${i === 0 && news.length > 2 ? "h-56" : "h-40"}`} />}
                  <div className="p-6 flex-1">
                    <span className="section-label text-sky-600">{n.category}</span>
                    <h3 className="font-heading font-bold text-slate-900 mt-2 group-hover:text-orange-600 transition-colors line-clamp-2 text-lg">{n.title}</h3>
                    <p className="text-sm text-slate-500 mt-2 line-clamp-2">{n.content}</p>
                    <div className="flex items-center justify-between mt-4">
                      <p className="font-mono text-xs text-slate-400">{new Date(n.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}</p>
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-orange-600 group-hover:gap-2 transition-all">Selengkapnya <span>→</span></span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={rise} className="relative bg-slate-950 text-white rounded-xl p-8 overflow-hidden" data-testid="emergency-contact-card">
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-orange-600/25 blur-3xl" />
            <Siren className="text-orange-500 mb-5 relative" size={30} />
            <h3 className="font-heading text-2xl font-bold relative">Kontak Darurat</h3>
            <p className="text-slate-400 text-sm mt-2 mb-7 relative">Hubungi kami segera jika terjadi bencana di wilayah Anda.</p>
            <div className="space-y-4 relative">
              <a href={`tel:${c("kontak_darurat", "112")}`} className="block border border-slate-800 hover:border-orange-500 rounded-xl p-4 transition-colors" data-testid="contact-112">
                <p className="section-label text-slate-400">Call Center</p>
                <p className="font-mono text-3xl font-bold text-orange-500 mt-1">{c("kontak_darurat", "112")}</p>
              </a>
              <div className="border border-slate-800 rounded-xl p-4">
                <p className="section-label text-slate-400">Posko BPBD (24 Jam)</p>
                <p className="font-mono text-lg font-bold mt-1">{c("kontak_telepon", "(0534) 771-112")}</p>
              </div>
              <div className="border border-slate-800 rounded-xl p-4">
                <p className="section-label text-slate-400">Email</p>
                <p className="font-mono text-sm mt-1 break-all">{c("kontak_email", "bpbd@kayongutarakab.go.id")}</p>
              </div>
            </div>
            <Link to="/layanan#lapor" className="btn-pop relative mt-7 block text-center px-4 py-3.5 bg-orange-600 hover:bg-orange-500 font-bold rounded-full" data-testid="emergency-lapor-btn">
              Lapor Sekarang
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Editorial marquee */}
      <div className="bg-slate-950 border-y border-slate-800 py-5 overflow-hidden" data-testid="editorial-marquee">
        <Marquee speed={32} gradient={false} className="editorial-marquee">
          {["Kenali Risikonya", "Siapkan Tas Siaga", "Pahami Jalur Evakuasi", "Tetap Tenang Saat Bencana", "Lapor Cepat ke 112", "Bersama Membangun Ketangguhan"].map((m) => (
            <span key={m} className="mx-6 inline-flex items-center gap-6 text-white/60 text-xl md:text-2xl font-bold uppercase">
              {m} <span className="text-orange-500">✦</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* Kegiatan BPBD */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-20">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="section-label text-sky-600 mb-2">Dokumentasi</p>
              <h2 className="font-heading text-lg font-bold text-slate-900">Kegiatan BPBD</h2>
            </div>
            <Link to="/berita?kategori=Kegiatan" className="group text-sm font-bold text-sky-600 hover:text-orange-600 inline-flex items-center gap-1.5 transition-colors" data-testid="home-semua-kegiatan">
              Semua Kegiatan <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          {kegiatan.length === 0 ? (
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-12 text-center text-slate-400" data-testid="kegiatan-empty">
              <Newspaper className="mx-auto mb-3" size={30} />
              Belum ada dokumentasi kegiatan. Tambahkan berita dengan kategori "Kegiatan" melalui panel admin.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="kegiatan-grid">
              {kegiatan.map((k, i) => (
                <motion.div key={k.id} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={rise}>
                  <Link to={`/berita/${k.id}`} className="group hover-lift block border border-slate-200 bg-white rounded-xl overflow-hidden hover:border-sky-400" data-testid={`kegiatan-card-${k.id}`}>
                    {k.image_url && <img src={k.image_url} alt={k.title} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" />}
                    <div className="p-6">
                      <span className="section-label text-sky-600">Kegiatan</span>
                      <h3 className="font-heading font-bold text-slate-900 mt-2 group-hover:text-sky-600 transition-colors line-clamp-2">{k.title}</h3>
                      <p className="font-mono text-xs text-slate-400 mt-3">{new Date(k.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}</p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Quick access */}
      <section className="bg-slate-950 relative overflow-hidden">
        <div className="blob absolute -top-32 right-1/4 w-96 h-96 rounded-full bg-sky-600/15 blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 py-20 relative">
          <p className="section-label text-orange-500 mb-2">Akses Cepat</p>
          <h2 className="font-heading text-lg font-bold text-white mb-10">Layanan Utama BPBD</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { to: "/layanan#lapor", icon: Siren, grad: "from-orange-600 to-red-600", title: "Lapor Bencana", desc: "Laporkan kejadian bencana di sekitar Anda" },
              { to: "/informasi", icon: Map, grad: "from-sky-600 to-blue-700", title: "Peta Rawan Bencana", desc: "Wilayah rawan banjir, longsor & karhutla" },
              { to: "/bidang", icon: ShieldCheck, grad: "from-emerald-600 to-teal-700", title: "Kesiapsiagaan", desc: "Panduan sebelum, saat & sesudah bencana" },
              { to: "/informasi-publik", icon: HeartHandshake, grad: "from-amber-500 to-orange-600", title: "PPID & Dokumen", desc: "Permohonan informasi & dokumen publik" },
            ].map((q, i) => (
              <motion.div key={q.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={rise}>
                <Link to={q.to} className="group hover-lift block h-full bg-slate-900 border border-slate-800 hover:border-slate-600 rounded-xl p-7" data-testid={`quick-${q.title.toLowerCase().replace(/\s|&/g, "-")}`}>
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${q.grad} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                    <q.icon className="text-white" size={24} />
                  </div>
                  <h3 className="font-heading font-bold text-white text-lg">{q.title}</h3>
                  <p className="text-sm text-slate-400 mt-2">{q.desc}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
